import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ArrowRight, AlertTriangle, ShieldCheck, Download } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import NextBestAction from "@/components/NextBestAction";
import usePageSEO from "@/hooks/usePageSEO";

/**
 * Phase 13.2 — vertical-specific small-business-owner sub-page.
 *
 * Content is curated per vertical with NAIC-compliant framing. No carrier
 * endorsements, no guaranteed outcomes.
 */

const VERTICAL_CONTENT = {
  hospitality: {
    hero: "Hospitality owners: protect the family and the property.",
    sub: "Hotels, motels, and B&Bs — where SBA loans, GM key-person risk, and seasonal cash flow intersect.",
    heroImg: "/assets/brand/vertical-hospitality.jpg",
    scenarios: [
      {
        title: "Owner dies unexpectedly — SBA 7(a) loan still owed on the motel",
        without: "The surviving spouse inherits the business + a personally-guaranteed SBA note. Lender calls the loan; family may have to sell the property under duress.",
        with: "Life insurance structured to pay off the SBA loan at death. Business continues. Family keeps optionality — operate, sell, or lease — on their terms.",
      },
      {
        title: "General Manager leaves with 90 days notice during peak season",
        without: "Revenue craters while owner works the front desk. No budget for an executive search firm. Reputation damage from slipping service.",
        with: "Key-person coverage on the GM (owned by the business) — provides a lump sum to fund recruitment, retention bonus for interim coverage, and a buffer against revenue dip.",
      },
      {
        title: "50/50 partners — one wants out",
        without: "Buyout negotiated under stress. Valuation disagreement. Either side can block operations while legal fees pile up.",
        with: "Pre-agreed buy-sell with funded life insurance on each partner. Clear valuation formula. Clean exit — at death, disability, or by choice.",
      },
    ],
    insights: [
      "SBA 7(a) loans typically carry personal guarantees — your life insurance plan needs to cover the note balance, not just income replacement.",
      "Key-person on the General Manager is often more valuable than on the owner for day-to-day continuity.",
      "Seasonal businesses benefit from Business Overhead Expense (BOE) coverage that triggers after a short elimination period.",
    ],
    carriers: ["Lincoln Financial", "Mutual of Omaha", "Nationwide", "Corebridge Financial"],
    chartData: [
      { year: "Y1", withPlan: 100, withoutPlan: 100 },
      { year: "Y2", withPlan: 115, withoutPlan: 70 },
      { year: "Y3", withPlan: 132, withoutPlan: 55 },
      { year: "Y4", withPlan: 152, withoutPlan: 48 },
      { year: "Y5", withPlan: 175, withoutPlan: 44 },
    ],
  },
  "gas-station-convenience": {
    hero: "Gas station & C-store owners: protect the family and the family business.",
    sub: "Most gas station and C-store operations are family businesses — one owner wears five hats and multi-location succession is messy.",
    heroImg: "/assets/brand/vertical-gas-station.jpg",
    scenarios: [
      {
        title: "Owner-operator sidelined by illness — single location, no backup",
        without: "Store runs on employees without owner supervision. Theft and shrinkage spike. Cash flow drops 30–40%.",
        with: "Disability income on the owner + Business Overhead Expense policy. Protects personal income AND business fixed costs during recovery.",
      },
      {
        title: "Three locations — siblings disagree on who runs what after dad retires",
        without: "Operational paralysis. Favoritism accusations. Eventual family-splitting sale at a discount.",
        with: "Buy-sell structured around the specific locations each sibling wants. Life insurance funds the cross-purchase. Clear governance before conflict erupts.",
      },
      {
        title: "Major equipment (pumps, cooler, POS) damaged and loan still outstanding",
        without: "Owner borrows against home or liquidates retirement savings to replace equipment.",
        with: "Keep business-owner life/DI/BOE separate from equipment financing; coordinate with your P&C broker on business property coverage.",
      },
    ],
    insights: [
      "Family-owned C-stores often skip succession planning until an event forces it; pre-agreed buy-sell is the single biggest value-saver.",
      "Multi-location operators benefit from entity-level planning — not just personal coverage on the owner.",
      "Gas station owners frequently carry personally-guaranteed equipment loans; life insurance should cover note balances specifically.",
    ],
    carriers: ["Mutual of Omaha", "Americo", "Corebridge Financial", "Nationwide"],
    chartData: [
      { year: "Y1", withPlan: 100, withoutPlan: 100 },
      { year: "Y2", withPlan: 112, withoutPlan: 65 },
      { year: "Y3", withPlan: 128, withoutPlan: 52 },
      { year: "Y4", withPlan: 148, withoutPlan: 42 },
      { year: "Y5", withPlan: 170, withoutPlan: 38 },
    ],
  },
  "liquor-retail": {
    hero: "Liquor & specialty retail: cash-heavy, license-sensitive, succession-critical.",
    sub: "Inventory-heavy, cash-handling-intensive, and the license alone can be worth more than the store.",
    heroImg: "/assets/brand/vertical-liquor-retail.jpg",
    scenarios: [
      {
        title: "Owner passes — state license transfer stalls",
        without: "Heirs can't legally operate. Store sits closed. License can lapse. Six-figure asset vanishes.",
        with: "Buy-sell with a successor named in advance. Life insurance funds immediate buyout so operations continue uninterrupted while licensing transitions.",
      },
      {
        title: "Inventory shrinkage or robbery — owner takes the hit personally",
        without: "Working capital depleted. Supplier terms tighten. Death spiral of margin compression.",
        with: "Strong P&C strategy coordinated with your personal income protection — if the owner is injured in a robbery, DI + BOE kick in.",
      },
      {
        title: "Second generation doesn't want the store — but needs the value",
        without: "Owner retires without planning; store is sold for a fraction of value because no outside buyer is ready.",
        with: "ESOP-lite or external buy-sell structured years in advance. Owner's retirement is funded by the sale, not dependent on it.",
      },
    ],
    insights: [
      "License transfer rules vary by state; plan for 30–120 day gaps in operating authority after owner death.",
      "Specialty retail owners often under-insure because the store feels 'small'; valuation methods frequently surprise them upward.",
      "Cash-handling risk calls for coordinated P&C + personal DI coverage, not just life insurance.",
    ],
    carriers: ["Lincoln Financial", "Mutual of Omaha", "Americo", "Ameritas"],
    chartData: [
      { year: "Y1", withPlan: 100, withoutPlan: 100 },
      { year: "Y2", withPlan: 110, withoutPlan: 60 },
      { year: "Y3", withPlan: 122, withoutPlan: 42 },
      { year: "Y4", withPlan: 140, withoutPlan: 35 },
      { year: "Y5", withPlan: 162, withoutPlan: 30 },
    ],
  },
  "restaurant-food-service": {
    hero: "Restaurants & food service: owner-operator economics, lease-heavy risk.",
    sub: "High-turnover staff, personally-guaranteed leases, equipment financing, and razor-thin margins.",
    heroImg: "/assets/brand/vertical-restaurant.jpg",
    scenarios: [
      {
        title: "Chef-owner can't cook — kitchen falls apart",
        without: "Revenue halves within weeks. Landlord calls personal guarantee on the lease. Equipment loans accelerate.",
        with: "Disability income with a short elimination period + BOE to cover rent/payroll/loan payments during recovery.",
      },
      {
        title: "Partners buy equipment on personal guarantees, one wants out",
        without: "Remaining partner inherits the debt and the operational load. Ex-partner's family sues for equity return.",
        with: "Buy-sell on each partner, pre-valued, funded with life insurance. Debt coordinated separately.",
      },
      {
        title: "Three locations, high debt, owner suddenly dies",
        without: "Lenders accelerate loans; landlord enforces personal guarantees against the estate; family loses everything.",
        with: "Life insurance sized to pay off accelerated debts + buy out partner's family + provide income replacement.",
      },
    ],
    insights: [
      "Personal guarantees on restaurant leases are common and can cascade through an owner's estate.",
      "DI with a short elimination period is especially valuable for owner-operators — missing 30 days can kill the business.",
      "Multi-unit operators need entity-level succession plans, not just personal coverage.",
    ],
    carriers: ["Lincoln Financial", "Mutual of Omaha", "Ameritas", "Corebridge Financial"],
    chartData: [
      { year: "Y1", withPlan: 100, withoutPlan: 100 },
      { year: "Y2", withPlan: 108, withoutPlan: 55 },
      { year: "Y3", withPlan: 118, withoutPlan: 38 },
      { year: "Y4", withPlan: 132, withoutPlan: 30 },
      { year: "Y5", withPlan: 148, withoutPlan: 26 },
    ],
  },
  general: {
    hero: "Small business owner planning — universal basics, done well.",
    sub: "Any owner, any industry. The four risks are the same; the coverage sizing is what changes.",
    heroImg: "/assets/brand/vertical-general.jpg",
    scenarios: [
      {
        title: "Sole owner dies — no written succession plan",
        without: "Family inherits an unsellable business. Employees quit. Clients leave. Asset depreciates to near-zero in 90 days.",
        with: "Written succession plan + funded life insurance + identified successor. Business continues or is sold at fair value.",
      },
      {
        title: "Co-owner disability — partnership stalemate",
        without: "Working partner carries 100% of load while paying 50% of distributions. Resentment and dissolution follow.",
        with: "Disability buy-out insurance triggers after a defined period. Working partner buys out disabled partner with proceeds.",
      },
      {
        title: "Owner retirement — company 401(k) was never set up",
        without: "Owner tries to live on the sale proceeds only. Tax hit on exit is brutal.",
        with: "Mix of qualified plan (SEP/Solo 401(k)) + non-qualified deferred comp + IUL for tax-free supplementation.",
      },
    ],
    insights: [
      "Every small business owner needs the same four conversations: buy-sell, key-person, overhead, retirement. The sizes differ — the categories don't.",
      "Coordinate with your CPA and attorney before any funded buy-sell; entity structure (S-corp vs LLC vs C-corp) changes what works.",
      "Review every 2–3 years or after any material business event (new partner, location, debt).",
    ],
    carriers: ["Lincoln Financial", "Mutual of Omaha", "Nationwide", "Corebridge Financial"],
    chartData: [
      { year: "Y1", withPlan: 100, withoutPlan: 100 },
      { year: "Y2", withPlan: 113, withoutPlan: 68 },
      { year: "Y3", withPlan: 128, withoutPlan: 50 },
      { year: "Y4", withPlan: 146, withoutPlan: 40 },
      { year: "Y5", withPlan: 166, withoutPlan: 35 },
    ],
  },
};

export default function BusinessOwnerVertical() {
  const { vertical } = useParams();
  const content = VERTICAL_CONTENT[vertical];

  usePageSEO({
    title: content
      ? `${content.hero.split("—")[0].trim()} | Business Owner Planning`
      : "Vertical not found",
    description: content ? content.sub : "Business owner vertical not found.",
  });

  if (!content) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="pt-32 pb-24 text-center">
          <h1 className="font-serif-display text-3xl text-lw-navy">Vertical not found</h1>
          <Link to="/for-business-owners" className="btn-ghost-navy mt-6 inline-flex">
            Back to Business Owners
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid={`bo-vertical-page-${vertical}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main>
        <section className="pt-28 sm:pt-32 pb-16 hero-grain">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <nav className="text-xs text-lw-muted mb-6">
              <Link to="/" className="hover:text-lw-navy">Home</Link>
              <span className="mx-1.5">/</span>
              <Link to="/for-business-owners" className="hover:text-lw-navy">Business Owners</Link>
              <span className="mx-1.5">/</span>
              <span className="text-lw-navy capitalize">{vertical.replace(/-/g, " ")}</span>
            </nav>
            <span className="lw-eyebrow">For Business Owners</span>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-lw-navy leading-[1.08]">
              {content.hero}
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-lw-muted">
              {content.sub}
            </p>
          </div>
        </section>

        {/* Scenarios */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <span className="lw-eyebrow">Common Scenarios</span>
            <h2 className="mt-3 font-serif-display text-3xl text-lw-navy">What usually happens — vs what we structure.</h2>
            <div className="mt-10 space-y-8">
              {content.scenarios.map((s, i) => (
                <article
                  key={i}
                  data-testid={`bo-scenario-${i}`}
                  className="rounded-2xl border border-lw-navy/10 bg-white overflow-hidden"
                >
                  <div className="px-6 sm:px-8 py-5 border-b border-lw-navy/10 bg-lw-ivory-warm">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={22} className="text-lw-gold mt-1 flex-shrink-0" />
                      <h3 className="font-serif-display text-xl text-lw-navy leading-tight">{s.title}</h3>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-lw-navy/10">
                    <div className="p-6 sm:p-8">
                      <div className="font-sans-ui text-[0.68rem] tracking-[0.2em] uppercase text-[#B91C1C] mb-2">Without planning</div>
                      <p className="text-sm leading-relaxed text-lw-muted">{s.without}</p>
                    </div>
                    <div className="p-6 sm:p-8 bg-lw-ivory-warm/50">
                      <div className="font-sans-ui text-[0.68rem] tracking-[0.2em] uppercase text-lw-green mb-2 flex items-center gap-1.5">
                        <ShieldCheck size={12} /> With Lifespan Partners
                      </div>
                      <p className="text-sm leading-relaxed text-lw-navy/90">{s.with}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Impact chart */}
        <section className="py-16 bg-lw-ivory-warm">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
            <span className="lw-eyebrow">Illustrative Impact</span>
            <h2 className="mt-3 font-serif-display text-3xl text-lw-navy">Business value, 5 years out.</h2>
            <p className="mt-3 text-sm text-lw-muted max-w-2xl">
              Indexed to 100 at year 1. Hand-set illustrative scenarios — not a projection.
            </p>
            <div className="mt-8 rounded-2xl border border-lw-navy/10 bg-white p-6">
              <ResponsiveContainer width="100%" height={320}>
                <RechartsBar data={content.chartData}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#0A2540" strokeOpacity="0.08" />
                  <XAxis dataKey="year" tick={{ fill: "#5A6472", fontSize: 12 }} stroke="#0A2540" strokeOpacity="0.2" />
                  <YAxis tick={{ fill: "#5A6472", fontSize: 12 }} stroke="#0A2540" strokeOpacity="0.2" />
                  <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                  <Bar dataKey="withoutPlan" fill="#9CA3AF" name="Without plan" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="withPlan" fill="#D4AF37" name="With Lifespan plan" radius={[4, 4, 0, 0]} />
                </RechartsBar>
              </ResponsiveContainer>
            </div>
            <p className="mt-4 text-xs text-lw-muted leading-relaxed">
              Illustrative example — not a guarantee or projection. Individual business
              outcomes depend on industry conditions, carrier underwriting, state
              availability, and many other factors. Insurance and annuity guarantees
              are backed by the financial strength and claims-paying ability of the
              issuing carrier.
            </p>
          </div>
        </section>

        {/* Insights */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
            <span className="lw-eyebrow">What We See In Practice</span>
            <h2 className="mt-3 font-serif-display text-3xl text-lw-navy">
              Operator-level insights.
            </h2>
            <ul className="mt-6 space-y-3">
              {content.insights.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-lw-ink">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-lw-gold flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Carriers */}
        <section className="py-14 bg-lw-ivory-warm">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <span className="lw-eyebrow">Where Our Carriers Come In</span>
            <h2 className="mt-3 font-serif-display text-2xl text-lw-navy">Carriers most relevant for this vertical.</h2>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {content.carriers.map((c) => (
                <li
                  key={c}
                  className="rounded-xl border border-lw-navy/10 bg-white p-4 text-center font-serif-display text-base text-lw-navy"
                >
                  {c}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-lw-muted">
              Carrier selection depends on your state, underwriting, and the specific
              product. Not an endorsement.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 gradient-navy-indigo">
          <div className="max-w-3xl mx-auto px-5 text-center">
            <h2 className="font-serif-display text-3xl text-white">Ready for a structured look at your business?</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to={`/apply?audience=business-owner&vertical=${vertical}`}
                data-testid="bo-vertical-cta-call"
                className="btn-gold"
              >
                Schedule a 15-min call <ArrowRight size={18} />
              </Link>
              <a
                href="/apply?audience=business-owner&source=pdf-checklist"
                data-testid="bo-vertical-cta-pdf"
                className="btn-ghost-light"
              >
                <Download size={16} /> Download the checklist
              </a>
            </div>
          </div>
        </section>

        <NextBestAction
          pageId={`business-owner-vertical-${vertical}`}
          step={3}
          heading={`Book a 15-minute ${content.hero.split("—")[0].trim().toLowerCase()} strategy call`}
          rationale={`You've seen the playbook for your industry. A 15-minute call confirms which pieces (buy-sell, key person, 162 bonus) apply to your entity structure — no obligation.`}
          primary={{ label: "Book a 15-min strategy call", to: `/apply?audience=business-owner&vertical=${vertical}`, testid: "nba-bov-primary" }}
          secondary={{ label: "See related carrier partners", to: "/carriers", testid: "nba-bov-secondary" }}
        />
      </main>
      <Footer />
    </div>
  );
}
